import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useId, useEffect, useRef } from "react";
import { useUiTranslation } from "../../i18n";
import { Button } from "../../components/base";
import { fieldRegistry } from "./fieldRegistry";
import { renderInteractionRequestField } from "./renderField";
import { FormImageLoaderContext } from "./FieldDescription";
function isThenable(value) {
    if (value === null)
        return false;
    const type = typeof value;
    if (type !== "object" && type !== "function")
        return false;
    const maybe = value;
    return typeof maybe.then === "function";
}
function normalizeOptionValue(field, value) {
    const options = field.options ?? [];
    if (field.type !== "radio" &&
        field.type !== "dropdown" &&
        field.type !== "checkbox") {
        return value;
    }
    const resolveSingleValue = (item) => {
        if (typeof item !== "string")
            return item;
        if (options.some((option) => option.value === item))
            return item;
        return options.find((option) => option.label === item)?.value ?? item;
    };
    if (field.type === "checkbox") {
        const items = Array.isArray(value) ? value : value == null ? [] : [value];
        return items.map(resolveSingleValue).filter(Boolean);
    }
    return resolveSingleValue(value);
}
/**
 * InteractionRequestForm - Runtime HITL form renderer for chat interfaces
 *
 * Renders a form based on the provided schema with support for:
 * - Text, numeric, choice, date, and file fields
 * - Validation (required fields, number constraints)
 * - Submit and reject actions
 * - Loading/submitting state
 *
 * @example
 * ```tsx
 * <InteractionRequestForm
 *   schema={{
 *     id: "confirm-action",
 *     title: "Please confirm",
 *     fields: [
 *       { name: "reason", label: "Reason", type: "shortText", required: true }
 *     ]
 *   }}
 *   onSubmit={(values) => console.log(values)}
 *   onReject={() => console.log("rejected")}
 * />
 * ```
 */
export const InteractionRequestForm = ({ schema, initialValues, onSubmit, onReject, readOnly = false, submitting = false, className, onPreviewLoad, }) => {
    const { t } = useUiTranslation();
    const formId = useId();
    // Initialize values from persisted values first, then schema defaults.
    const resolvedInitialValues = useMemo(() => {
        const values = {};
        schema.fields.forEach((field) => {
            const labelKey = (field.label ?? "").toString().trim();
            const submittedValue = initialValues?.[field.name] ??
                (labelKey ? initialValues?.[labelKey] : undefined);
            if (submittedValue !== undefined) {
                values[field.name] = normalizeOptionValue(field, submittedValue);
            }
            else if (field.type !== "file" &&
                field.defaultValue !== undefined &&
                field.defaultValue !== null) {
                // For checkbox, ensure array format and filter out falsy values
                if (field.type === "checkbox") {
                    const arr = Array.isArray(field.defaultValue)
                        ? field.defaultValue
                        : [field.defaultValue];
                    values[field.name] = normalizeOptionValue(field, arr.filter(Boolean));
                }
                else {
                    values[field.name] = normalizeOptionValue(field, field.defaultValue);
                }
            }
            else if (field.type === "checkbox") {
                values[field.name] = [];
            }
            else {
                values[field.name] = "";
            }
        });
        return values;
    }, [initialValues, schema.fields]);
    const [values, setValues] = useState(resolvedInitialValues);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isBusy = readOnly || submitting || isSubmitting;
    useEffect(() => {
        setValues(resolvedInitialValues);
        setErrors({});
    }, [resolvedInitialValues]);
    // If the parent controls submitting state, unlock once it transitions from true -> false.
    // This avoids a permanently-disabled form after async completion.
    const prevSubmittingRef = useRef(submitting);
    useEffect(() => {
        if (prevSubmittingRef.current === true && submitting === false) {
            setIsSubmitting(false);
        }
        prevSubmittingRef.current = submitting;
    }, [submitting]);
    const handleFieldChange = useCallback((fieldName, value) => {
        setValues((prev) => ({ ...prev, [fieldName]: value }));
        // Clear error when user modifies the field
        setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
    }, []);
    const validate = useCallback(() => {
        const nextErrors = {};
        for (const field of schema.fields) {
            const spec = fieldRegistry[field.type];
            if (!spec?.validator)
                continue;
            const error = spec.validator(field, values[field.name], t);
            if (error) {
                nextErrors[field.name] = error;
            }
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }, [schema.fields, values, t]);
    const buildSubmission = useCallback(() => {
        const submission = {};
        schema.fields.forEach((field) => {
            const rawValue = values[field.name];
            const spec = fieldRegistry[field.type];
            // Use serializer if available, otherwise use raw value
            const serialized = spec?.serializer
                ? spec.serializer(field, rawValue)
                : rawValue;
            // Skip undefined/null/empty values (following web behavior)
            if (serialized === undefined || serialized === null)
                return;
            if (serialized === "")
                return;
            if (Array.isArray(serialized) && serialized.length === 0)
                return;
            // Prefer human-defined label as the submission key; fall back to field name.
            const keySource = (field.label ?? "").toString().trim();
            const key = keySource.length > 0 ? keySource : field.name;
            submission[key] = serialized;
        });
        return submission;
    }, [schema.fields, values]);
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (isBusy)
            return;
        if (!validate())
            return;
        setIsSubmitting(true);
        const submission = buildSubmission();
        const result = onSubmit(submission);
        if (isThenable(result)) {
            try {
                await result;
            }
            finally {
                setIsSubmitting(false);
            }
        }
        else {
            // Sync submit: keep disabled (prevents double-submit) until either the parent
            // toggles `submitting` (controlled) or the form unmounts (common in chat flows).
        }
    }, [isBusy, validate, buildSubmission, onSubmit]);
    const handleReject = useCallback(async () => {
        if (isBusy)
            return;
        setIsSubmitting(true);
        const result = onReject?.();
        if (isThenable(result)) {
            try {
                await result;
            }
            finally {
                setIsSubmitting(false);
            }
        }
        else {
            // Sync reject: keep disabled until parent toggles `submitting` or unmount.
        }
    }, [isBusy, onReject]);
    return (_jsx("div", { className: className, children: _jsxs("div", { className: "rounded-md border p-4 bg-background", children: [_jsx("div", { className: "text-lg font-medium mb-2", children: schema.title }), schema.description && (_jsx("p", { className: "text-sm text-muted-foreground mb-4 whitespace-pre-wrap", children: schema.description })), _jsxs("form", { onSubmit: handleSubmit, noValidate: true, children: [_jsx(FormImageLoaderContext.Provider, { value: onPreviewLoad, children: _jsx("div", { className: "space-y-4", children: schema.fields.map((field) => {
                                    return renderInteractionRequestField({
                                        key: `${formId}-${field.name}`,
                                        field,
                                        rawValue: values[field.name],
                                        disabled: isBusy,
                                        error: errors[field.name],
                                        onFieldChange: handleFieldChange,
                                    });
                                }) }) }), !readOnly && (_jsxs("div", { className: "mt-6 flex items-center gap-2", children: [onReject && (_jsx(Button, { type: "button", variant: "ghost", onClick: handleReject, disabled: isBusy, children: t("form.reject") })), _jsx(Button, { type: "submit", disabled: isBusy, children: t(isBusy &&
                                        Object.values(values).some((value) => value instanceof File)
                                        ? "form.file.uploading"
                                        : "form.submit") })] }))] })] }) }));
};
export default InteractionRequestForm;
//# sourceMappingURL=InteractionRequestForm.js.map